// The coach's working item while it writes a plan (spec §13.10, §13.15): a
// line per step as code reports it (`run.status`), each finding as it is
// reached (`coach.insight`), and the phases and sessions so far. While the
// run works it is expanded; once the plan is proposed it collapses to one
// line ("Looked at your training · 3 findings") that opens again on a tap.
// The reasoning itself is the proposal's "How I planned this".
import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CoachCard, Eyebrow } from './coachCard';

// A step running this long gets a quiet line: during a 100 s stall nothing
// moved, and the athlete took an old message for the reply (29 Oct review).
export const SLOW_STEP_MS = 30 * 1000;
export const SLOW_STEP_TEXT = 'Still on it — this is taking longer than usual.';

/** True once the step named `stepKey` has been the current one for SLOW_STEP_MS (counted from when this screen saw it start). */
export function useSlowStep(stepKey, active, ms = SLOW_STEP_MS) {
  const [slowKey, setSlowKey] = useState(null);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setSlowKey(stepKey), ms);
    return () => clearTimeout(t);
  }, [stepKey, active, ms]);
  return active && slowKey === stepKey;
}

export default function CreationWork({ view, slowAfterMs = SLOW_STEP_MS }) {
  const [open, setOpen] = useState(null);           // null: follow the run (open while working)
  const steps = view?.steps || [];
  const last = steps[steps.length - 1];
  const slow = useSlowStep(last ? `${steps.length}:${last.stage}:${last.label}` : 'starting', Boolean(view && !view.done), slowAfterMs);
  if (!view) return null;
  const expanded = open ?? !view.done;
  const { insights } = view;
  const summary = view.done
    ? `Looked at your training${insights.length ? ` · ${insights.length} finding${insights.length === 1 ? '' : 's'}` : ''}`
    : last?.label || view.startingLabel || 'Starting…';
  return (
    <CoachCard tone={view.done ? 'plain' : 'accent'} role="status" aria-live="polite"
      aria-label={view.done ? summary : `Your coach is working: ${summary}`}>
      <button type="button" onClick={() => setOpen(!expanded)} aria-expanded={expanded}
        className="-my-1 flex min-h-[28px] w-full items-center gap-2 text-left">
        {view.done
          ? <><Check size={14} className="shrink-0 text-accent-default" /><span className="flex-1 truncate type-label text-text-primary">{summary}</span></>
          : <Eyebrow className="flex-1">Working</Eyebrow>}
        {expanded ? <ChevronDown size={14} className="shrink-0 text-text-tertiary" /> : <ChevronRight size={14} className="shrink-0 text-text-tertiary" />}
      </button>
      {!view.done && !expanded && <p className="m-0 truncate type-body-small text-text-primary">{summary}</p>}
      {expanded && (
        <div className="space-y-1.5">
          {!steps.length && !view.done && (
            <p className="m-0 flex items-center gap-2 type-body-small text-text-secondary">
              <Loader2 size={12} className="shrink-0 animate-spin text-accent-default" /> {summary}
            </p>
          )}
          {steps.map((s, i) => {
            const current = !view.done && i === steps.length - 1;
            return (
              <p key={s.stage} className={cn('m-0 flex items-center gap-2 type-body-small', current ? 'text-text-secondary' : 'text-text-primary')}>
                {current
                  ? <span className="h-2 w-2 shrink-0 animate-pulse rounded-full border border-accent-default" aria-hidden="true" />
                  : <span className="h-2 w-2 shrink-0 rounded-full bg-text-primary" aria-hidden="true" />}
                {s.label}
              </p>
            );
          })}
          {insights.length > 0 && (
            <ul className="m-0 list-none space-y-0.5 p-0">
              {insights.map((x, i) => <li key={i} className="type-caption text-text-body">• {x.text}</li>)}
            </ul>
          )}
          {(view.phases.length > 0 || view.sessions > 0) && (
            <p className="m-0 pt-0.5 type-caption text-text-tertiary">
              {view.phases.length > 0 && view.phases.map((p) => p.name).join(' → ')}
              {view.phases.length > 0 && view.sessions > 0 && ' · '}
              {view.sessions > 0 && `${view.sessions} session${view.sessions === 1 ? '' : 's'} on your calendar, marked as proposed`}
            </p>
          )}
        </div>
      )}
      {slow && <p className="m-0 type-caption text-text-tertiary">{SLOW_STEP_TEXT}</p>}
    </CoachCard>
  );
}
